/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Label } from '../ui/Label';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Checkbox } from 'antd';
import { RoleService } from '../../services/role.service';
import { FolderService } from '../../services/folder.service';
import { FileService } from '../../services/file.service';
import { CustomSelect } from '../ui/Select';
import { X } from 'lucide-react';

const INITIAL_PERMISSION_MAP: Record<'folder' | 'file', string[]> = { folder: [], file: [] };

type ResourcePermission = {
  type: 'folder' | 'file';
  id: string | null;
  name: string;
  access: Record<string, boolean>;
};

type SelectOption = {
  label: string;
  value: string;
};

type RoleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingRole?: any | null;
};

export const RoleModal = ({ isOpen, onClose, onSuccess, editingRole }: RoleModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fileOptions, setFileOptions] = useState<SelectOption[]>([]);
  const [folderOptions, setFolderOptions] = useState<SelectOption[]>([]);
  const [permissions, setPermissions] = useState<ResourcePermission[]>([]);
  const [permissionMap, setPermissionMap] = useState(INITIAL_PERMISSION_MAP);
  const [nameError, setNameError] = useState('');
  const [initialState, setInitialState] = useState<any>(null);

  const flattenFolders = (folders: any[], prefix = ''): SelectOption[] =>
    folders.flatMap((folder: any) => {
      const label = prefix ? `${prefix} > ${folder.folderName}` : folder.folderName;
      const children = folder.subfolders ? flattenFolders(folder.subfolders, label) : [];
      return [{ label, value: folder.id }, ...children];
    });

  const loadData = async () => {
    const [folders, files, rawPermissions] = await Promise.all([
      FolderService.getAllWithHierarchy(),
      FileService.getAllFiles(),
      RoleService.getPermissions(),
    ]);

    const flatFolders = flattenFolders(folders.filter((f: any) => f.folderName !== 'Uncategorized'));
    setFolderOptions([{ label: 'All Folders', value: 'ALL' }, ...flatFolders]);
    setFileOptions([
      { label: 'All Files', value: 'ALL' },
      ...(files.data || []).map((file: any) => ({
        label: file.editedName || file.fileName,
        value: file.id,
      })),
    ]);

    const map: Record<'folder' | 'file', string[]> = { folder: [], file: [] };
    (rawPermissions.data || []).forEach((perm: any) => {
      const [type, action] = perm.name.split(':');
      if ((type === 'folder' || type === 'file') && action) {
        map[type as 'folder' | 'file'].push(action);
      }
    });

    setPermissionMap(map);

    if (editingRole) {
      setName(editingRole.name);
      setDescription(editingRole.description || '');
      setPermissions(editingRole.resourcePermissions || []);
      setInitialState({
        name: editingRole.name,
        description: editingRole.description || '',
        permissions: JSON.stringify(editingRole.resourcePermissions || []),
      });
    }
  };

  useEffect(() => {
    if (isOpen) loadData();
    else {
      setName('');
      setDescription('');
      setPermissions([]);
      setNameError('');
    }
  }, [isOpen]);

  const handleAdd = () => {
    setPermissions((prev) => [
      ...prev,
      {
        type: 'folder',
        id: '',
        name: '',
        access: Object.fromEntries((permissionMap['folder'] || []).map((p) => [p, false])),
      },
    ]);
  };

  const handleRemovePermission = (index: number) => {
    const updated = [...permissions];
    updated.splice(index, 1);
    setPermissions(updated);
  };

  const isUnchanged = () => {
    if (!editingRole) return false;
    return (
      name === initialState?.name &&
      description === initialState?.description &&
      JSON.stringify(permissions) === initialState?.permissions
    );
  };

  const updatePermission = (index: number, field: keyof ResourcePermission, value: any) => {
    const updated = [...permissions];
    const current = updated[index];

    if (field === 'type') {
      current.type = value;
      current.id = '';
      current.name = '';
      current.access = Object.fromEntries(
        (permissionMap[value as 'folder' | 'file'] || []).map((p) => [p, false]),
      );
    } else if (field === 'id') {
      const list = current.type === 'folder' ? folderOptions : fileOptions;
      const selected = list.find((item) => item.value === value);
      current.id = value;
      current.name = selected?.label || '';
    }

    setPermissions(updated);
  };

  const updateAccess = (index: number, perm: string, checked: boolean) => {
    const updated = [...permissions];
    const current = updated[index];
    current.access[perm] = checked;

    if ((perm === 'create' || perm === 'upload') && checked) {
      Object.keys(current.access).forEach((key) => {
        if (key !== perm) current.access[key] = false;
      });
      current.id = 'ALL';
      current.name = current.type === 'folder' ? 'All Folders' : 'All Files';
    }

    setPermissions(updated);
  };

  const hasAtLeastOnePermission =
    permissions.length > 0 &&
    permissions.every((perm) => Object.values(perm.access).some((val) => val));

  const handleSubmit = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setNameError('Role name is required.');
      return;
    }

    if (!hasAtLeastOnePermission) return;

    const payload = {
      name: trimmedName,
      description: description.trim(),
      resourcePermissions: permissions,
    };

    try {
      if (editingRole) {
        await RoleService.update(editingRole.id, payload);
      } else {
        await RoleService.create(payload);
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error('[RoleModal] Error saving role:', err);
    }
  };

  return (
   <Modal
  isOpen={isOpen}
  onClose={onClose}
  title={editingRole ? 'Edit Role' : 'Create Role'}
>
  <div className="max-h-[50vh] overflow-y-auto pr-2">
        <Label>Role Name</Label>
        <Input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError('');
          }}
          placeholder="e.g. Editor"
          error={!!nameError}
        />
        {nameError && <p className="mt-1 text-sm text-red-500">{nameError}</p>}

        <Label className="mt-4">Description</Label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional"
        />

        <Label className="mt-4">Permissions</Label>

        {permissions.map((perm, idx) => {
          const isGlobal = perm.access['create'] || perm.access['upload'];
          const isLast = idx === permissions.length - 1;

          return (
            <div
              key={idx}
              className={`mb-3 pb-2 ${!isLast ? 'border-b' : ''}`}
            >
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <select
                  className="px-2 py-1 border rounded"
                  value={perm.type}
                  onChange={(e) => updatePermission(idx, 'type', e.target.value)}
                >
                  <option value="folder">Folder</option>
                  <option value="file">File</option>
                </select>

                <div className="min-w-[240px]">
                  <CustomSelect
                    data={perm.type === 'folder' ? folderOptions : fileOptions}
                    value={perm.id || ''}
                    onChange={(val) => updatePermission(idx, 'id', val)}
                    placeholder={`Select a ${perm.type}`}
                    disabled={isGlobal}
                  />
                </div>

                <button onClick={() => handleRemovePermission(idx)}>
                  <X className="w-4 h-4 text-red-500" />
                </button>
              </div>

              <div className="flex flex-wrap gap-3">
                {(permissionMap[perm.type] || []).map((p) => {
                  const isExclusive = perm.access['create'] || perm.access['upload'];
                  const isDisabled = isExclusive && p !== 'create' && p !== 'upload';
                  return (
                    <Checkbox
                      key={p}
                      checked={perm.access[p]}
                      disabled={isDisabled}
                      onChange={(e) => updateAccess(idx, p, e.target.checked)}
                    >
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </Checkbox>
                  );
                })}
              </div>
            </div>
          );
        })}

        <Button className="mt-1 px-7 sm:px-6" onClick={handleAdd}>
          + Add
        </Button>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="secondary" onClick={onClose} className="px-7 sm:px-10">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          className="px-7 sm:px-10"
          disabled={!hasAtLeastOnePermission || !name.trim() || isUnchanged()}
        >
          {editingRole ? 'Save' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
};
